/**
 * DelegationsPage — J7-W3 task 3a (PRD §12.3.10)
 *
 * Lists every delegation in the active org. Columns: delegator, delegate,
 * scope, domains (when scope='domain'), starts_at, expires_at, status,
 * linked Decision Note. Filters by status. "+ New Delegation" CTA opens
 * DelegationFormDialog. Per-row "Revoke" action gated on status='active'
 * + the Owner/Admin permission chain on the route (route enforces RBAC).
 *
 * GOV-05 — every create/revoke is audit-logged via the actors.events bus.
 * GOV-06 — scope check is enforced server-side in
 * ActorsService.createDelegation; this page only surfaces the resulting
 * 403 as an inline error.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ScrollText, Plus, ShieldCheck } from 'lucide-react';
import { t } from '@/lib/i18n';
import { RorosPage } from '@/components/roros/RorosPage';
import { QueryPage } from '@/components/QueryPage';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Select } from '@/components/ui/Select';
import { showToast } from '@/components/ui/Toast';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import {
  useRorosDelegations,
  useRorosDelegationRevoke,
} from '@/hooks/roros/delegations';
import { useRorosActors } from '@/hooks/roros/actors';
import { useRorosDomains } from '@/hooks/roros/domains';
import { formatDate } from '@/lib/localeFormatters';
import { DelegationFormDialog } from './DelegationFormDialog';
import type { RorosDelegationDTO, RorosActorDTO } from '@/types/roros/actors';
import type { RorosDomainDTO } from '@/types/roros/domains';

function statusBadge(status: string): React.ReactNode {
  switch (status) {
    case 'active':
      return <Badge variant="success">{t('Active')}</Badge>;
    case 'expired':
      return <Badge variant="secondary">{t('Expired')}</Badge>;
    case 'revoked':
      return <Badge variant="error">{t('Revoked')}</Badge>;
    default:
      return <Badge variant="secondary">{status}</Badge>;
  }
}

function scopeBadge(scope: string): React.ReactNode {
  switch (scope) {
    case 'full':
      return <Badge variant="outline">{t('Full')}</Badge>;
    case 'domain':
      return <Badge variant="outline">{t('Domain')}</Badge>;
    case 'custom':
      return <Badge variant="outline">{t('Custom')}</Badge>;
    default:
      return <Badge variant="outline">{scope}</Badge>;
  }
}

export function DelegationsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<RorosDelegationDTO | null>(null);

  // Allow /roros/delegations/new to deep-link straight into the create dialog.
  useEffect(() => {
    if (location.pathname.endsWith('/delegations/new')) {
      setIsFormOpen(true);
    }
  }, [location.pathname]);

  const params = useMemo(() => {
    if (statusFilter === 'all') return undefined;
    return { status: statusFilter };
  }, [statusFilter]);

  const delegationsQuery = useRorosDelegations(params);
  const actorsQuery = useRorosActors();
  const domainsQuery = useRorosDomains();
  const revokeMutation = useRorosDelegationRevoke();

  const actorMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of (actorsQuery.data as RorosActorDTO[] | undefined) ?? []) {
      m.set(a.id, a.name);
    }
    return m;
  }, [actorsQuery.data]);

  const domainMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const d of (domainsQuery.data as RorosDomainDTO[] | undefined) ?? []) {
      m.set(d.id, d.name);
    }
    return m;
  }, [domainsQuery.data]);

  const handleRevokeConfirm = () => {
    if (!revokeTarget) return;
    revokeMutation.mutate(revokeTarget.id, {
      onSuccess: () => {
        showToast.success(t('Delegation revoked'));
        setRevokeTarget(null);
      },
      onError: (err: Error) => {
        showToast.error(err.message || t('Failed to revoke delegation'));
      },
    });
  };

  return (
    <RorosPage
      title={t('Delegations')}
      icon={<ShieldCheck className="h-5 w-5" aria-hidden />}
      actions={
        <Button
          onClick={() => setIsFormOpen(true)}
          leftIcon={<Plus className="h-4 w-4" />}
          data-testid="delegations-new-button"
        >
          {t('New Delegation')}
        </Button>
      }
    >
      <div className="mt-4 flex flex-wrap gap-3">
        <Select
          label={t('Status')}
          value={statusFilter}
          onValueChange={setStatusFilter}
          options={[
            { value: 'all', label: t('All statuses') },
            { value: 'active', label: t('Active') },
            { value: 'expired', label: t('Expired') },
            { value: 'revoked', label: t('Revoked') },
          ]}
        />
      </div>

      <div className="mt-6">
        <QueryPage
          query={delegationsQuery}
          emptyMessage={t('No delegations yet. Create one to hand off decision rights.')}
          emptyIcon={<ScrollText className="h-8 w-8" />}
          emptyAction={
            <Button onClick={() => setIsFormOpen(true)} leftIcon={<Plus className="h-4 w-4" />}>
              {t('Create your first delegation')}
            </Button>
          }
        >
          {(delegations) => {
            const list = delegations as RorosDelegationDTO[];
            if (list.length === 0) {
              return (
                <div className="text-center text-sm text-muted-foreground py-8">
                  {t('No delegations match the current filters.')}
                </div>
              );
            }
            return (
              <div className="overflow-x-auto rounded-lg border border-border">
                <table
                  className="w-full min-w-[640px] text-sm"
                  aria-label={t('Delegations')}
                  data-testid="delegations-table"
                >
                  <thead>
                    <tr className="border-b border-border bg-muted/40">
                      <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-2.5">{t('Delegator')}</th>
                      <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-2.5">{t('Delegate')}</th>
                      <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-2.5 w-24">{t('Scope')}</th>
                      <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-2.5">{t('Domains')}</th>
                      <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-2.5 w-28">{t('Starts')}</th>
                      <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-2.5 w-28">{t('Expires')}</th>
                      <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-2.5 w-24">{t('Status')}</th>
                      <th className="text-right text-xs font-semibold text-muted-foreground px-4 py-2.5 w-32">{t('Actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((d) => (
                      <tr
                        key={d.id}
                        className="border-b border-border last:border-0 hover:bg-muted/20 transition-colors"
                        data-testid="delegation-row"
                      >
                        <td className="px-4 py-3 font-medium text-foreground">
                          {actorMap.get(d.delegatorActorId) ?? d.delegatorActorId.slice(0, 8)}
                        </td>
                        <td className="px-4 py-3 text-foreground">
                          {actorMap.get(d.delegateActorId) ?? d.delegateActorId.slice(0, 8)}
                        </td>
                        <td className="px-4 py-3">{scopeBadge(d.scope)}</td>
                        <td className="px-4 py-3 text-muted-foreground text-xs">
                          {d.scope === 'domain' && d.domainIds && d.domainIds.length > 0
                            ? d.domainIds.map((id) => domainMap.get(id) ?? id.slice(0, 8)).join(', ')
                            : t('—')}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground text-xs">
                          {d.startsAt ? formatDate(new Date(d.startsAt)) : '—'}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground text-xs">
                          {d.expiresAt ? formatDate(new Date(d.expiresAt)) : '—'}
                        </td>
                        <td className="px-4 py-3">{statusBadge(d.status)}</td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => navigate(`/roros/delegations/${d.id}`)}
                              data-testid="delegation-view-button"
                            >
                              {t('View')}
                            </Button>
                            {d.status === 'active' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setRevokeTarget(d)}
                                data-testid="delegation-revoke-button"
                              >
                                {t('Revoke')}
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          }}
        </QueryPage>
      </div>

      <DelegationFormDialog
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onCreated={() => delegationsQuery.refetch()}
      />

      <ConfirmDialog
        isOpen={!!revokeTarget}
        onClose={() => setRevokeTarget(null)}
        onConfirm={handleRevokeConfirm}
        title={t('Revoke this delegation?')}
        description={t('Revoking will immediately end the delegation. This cannot be undone (GOV-05 audit event recorded).')}
        variant="destructive"
        confirmText={t('Revoke')}
        isLoading={revokeMutation.isPending}
      />
    </RorosPage>
  );
}
